import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/core/utils';
import { describeItem } from '../describeItem';
import type { SearchItem } from '../types';

interface Props {
  items: SearchItem[];
  message: string | null;
  active: number;
  onHighlight: (index: number) => void;
  onSelect: (item: SearchItem) => void;
}

export function SearchResults({ items, message, active, onHighlight, onSelect }: Props) {
  const { t } = useTranslation('search');
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  return (
    <ul
      ref={listRef}
      role="listbox"
      className="absolute top-[calc(100%+6px)] left-0 z-3 m-0 max-h-[min(60dvh,26rem)] w-full list-none overflow-y-auto overscroll-contain rounded-xl bg-white py-1 shadow-[0_4px_16px_rgba(0,0,0,.25)]"
    >
      {!items.length && <li className="cursor-default px-4 py-3 text-hint">{message}</li>}
      {items.map((it, i) => (
        <li
          key={`${it.name}|${i}`}
          role="option"
          aria-selected={i === active}
          className={cx('cursor-pointer px-4 py-2.5 md:px-3 md:py-1.5', i === active && 'bg-accent-soft')}
          onMouseDown={(e) => {
            e.preventDefault(); // keep focus in the input
            onSelect(it);
          }}
          onMouseMove={() => onHighlight(i)}
        >
          <div>{it.name}</div>
          <div className="text-sm text-hint md:text-xs">{describeItem(t, it)}</div>
        </li>
      ))}
    </ul>
  );
}
